// Pure portfolio-performance calculations (Sharesight-style metrics).
//
// All functions operate on transaction documents as stored in
// userdata/{uid}/transactions by importPortfolioData:
//   { type, buySell, ticker, quantity, pricePerShare, totalAmount, timestamp,
//     instrumentCurrency, accountCurrency, dividend* fields, ... }
//
// Cost bases and gains are computed in the instrument currency (pricePerShare),
// which matches the currency of cached market prices after normalizeQuote().
// Known limitation: FIFO lot matching does not adjust for stock splits that
// occur between a buy and a sell (total cost base is unaffected by splits,
// but per-disposal gains for split stocks may be approximate).

const EPSILON = 1e-9;

// Yahoo quotes LSE stocks in pence (GBp/GBX); convert to pounds so prices,
// values and cost bases (Freetrade reports LSE prices in GBP) are comparable.
export function normalizeQuote(price, currency) {
  if (price !== null && price !== undefined && (currency === "GBp" || currency === "GBX")) {
    return { price: price / 100, currency: "GBP" };
  }
  return { price, currency };
}

const INCOME_TYPES = new Set(["DIVIDEND", "INTEREST", "INTEREST_FROM_CASH"]);

// Gross/net amounts for an income transaction, in the account currency.
// UK dividends carry no withholding tax, so Freetrade leaves the Gross/Net
// Distribution columns blank — and an earlier import bug stored those blanks
// as 0 — so both null and 0 are treated as missing. Fallbacks: the cash
// actually credited (totalAmount), then per-share × eligible quantity.
// Returns { gross, net }, each null when nothing usable is present.
export function incomeAmounts(tx) {
  const present = (v) => Number.isFinite(v) && Math.abs(v) > EPSILON;
  const grossRaw = present(tx?.dividendGrossAmount) ? Math.abs(tx.dividendGrossAmount) : null;
  const netRaw = present(tx?.dividendNetAmount) ? Math.abs(tx.dividendNetAmount) : null;
  const cash = present(tx?.totalAmount) ? Math.abs(tx.totalAmount) : null;
  const derived =
    present(tx?.dividendAmountPerShare) && present(tx?.dividendEligibleQuantity)
      ? Math.abs(tx.dividendAmountPerShare * tx.dividendEligibleQuantity)
      : null;
  const net = netRaw ?? cash ?? grossRaw ?? derived;
  const gross = grossRaw ?? net;
  return { gross, net };
}

// Brokers use several dividend-like types beyond plain DIVIDEND — e.g.
// SPECIAL_DIVIDEND and REIT property income distributions. Treat anything in
// the dividend/distribution family as income.
export function isIncomeType(type) {
  if (!type) return false;
  return INCOME_TYPES.has(type) || /DIVIDEND|DISTRIBUTION/.test(type);
}

// FIFO walk over one ticker's ORDER transactions, plus actual income from
// the broker's DIVIDEND/INTEREST rows.
//
// Two parallel cost bases are tracked:
//  - costBasis: pricePerShare × quantity, in the instrument currency.
//  - costBasisAccount: the broker's totalAmount (cash actually debited, in
//    the account currency, at the FX rate of the purchase date). Null if any
//    open lot is missing totalAmount, so callers can fall back cleanly.
export function computeHoldingMetrics(transactions) {
  const orders = (transactions || [])
    .filter((tx) => tx.type === "ORDER" && (tx.quantity || 0) > 0)
    .slice()
    .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

  const lots = [];
  const disposals = [];
  let realizedGain = 0;
  let realizedGainAccount = 0;
  let realizedAccountComplete = true;
  let buyCost = 0;
  let sellProceeds = 0;
  let firstBuyTs = null;
  let currency = null;
  let accountCurrency = null;

  for (const tx of orders) {
    const qty = tx.quantity || 0;
    const price = tx.pricePerShare ?? 0;
    if (!currency && tx.instrumentCurrency) currency = tx.instrumentCurrency;
    if (!accountCurrency && tx.accountCurrency) accountCurrency = tx.accountCurrency;
    const totalAmount = Number.isFinite(tx.totalAmount) ? Math.abs(tx.totalAmount) : null;

    if (tx.buySell === "BUY") {
      lots.push({
        quantity: qty,
        costPerShare: price,
        // Account-currency cost per share, from cash actually debited.
        basePerShare: totalAmount !== null && qty > EPSILON ? totalAmount / qty : null,
        timestamp: tx.timestamp || 0,
      });
      buyCost += qty * price;
      if (firstBuyTs === null) firstBuyTs = tx.timestamp || 0;
    } else if (tx.buySell === "SELL") {
      let remaining = qty;
      let cost = 0;
      let costAccount = 0;
      let costAccountComplete = true;
      const buyDates = [];
      while (remaining > EPSILON && lots.length > 0) {
        const lot = lots[0];
        const used = Math.min(lot.quantity, remaining);
        cost += used * lot.costPerShare;
        if (lot.basePerShare !== null) costAccount += used * lot.basePerShare;
        else costAccountComplete = false;
        buyDates.push(lot.timestamp);
        lot.quantity -= used;
        remaining -= used;
        if (lot.quantity <= EPSILON) lots.shift();
      }
      // Unmatched quantity (e.g. shares received via a split) carries zero cost.
      const proceeds = qty * price;
      const gain = proceeds - cost;
      realizedGain += gain;
      sellProceeds += proceeds;
      if (totalAmount !== null && costAccountComplete) {
        realizedGainAccount += totalAmount - costAccount;
      } else {
        realizedAccountComplete = false;
      }
      disposals.push({
        timestamp: tx.timestamp || 0,
        quantity: qty,
        proceeds,
        cost,
        gain,
        buyDates,
      });
    }
  }

  const quantity = lots.reduce((sum, lot) => sum + lot.quantity, 0);
  const costBasis = lots.reduce((sum, lot) => sum + lot.quantity * lot.costPerShare, 0);
  const accountComplete = lots.every((lot) => lot.basePerShare !== null);
  const costBasisAccount = accountComplete
    ? lots.reduce((sum, lot) => sum + lot.quantity * (lot.basePerShare || 0), 0)
    : null;

  // Actual broker-reported income for this ticker (account currency).
  let incomeNet = 0;
  let incomeGross = 0;
  let hasIncome = false;
  for (const tx of transactions || []) {
    if (!isIncomeType(tx.type)) continue;
    const { gross, net } = incomeAmounts(tx);
    if (net === null) continue;
    hasIncome = true;
    incomeNet += net;
    incomeGross += gross;
    if (!accountCurrency && tx.accountCurrency) accountCurrency = tx.accountCurrency;
  }

  return {
    quantity,
    costBasis,
    costBasisAccount,
    avgCost: quantity > EPSILON ? costBasis / quantity : null,
    realizedGain,
    realizedGainAccount: realizedAccountComplete ? realizedGainAccount : null,
    disposals,
    buyCost,
    sellProceeds,
    firstBuyTs,
    currency,
    accountCurrency,
    incomeNet: hasIncome ? incomeNet : null,
    incomeGross: hasIncome ? incomeGross : null,
  };
}

// Groups the full transaction history by ticker and computes holding metrics
// for each. Returns { [ticker]: metrics }.
export function computeAllHoldingMetrics(transactions) {
  const byTicker = {};
  for (const tx of transactions || []) {
    if (!tx.ticker) continue;
    if (tx.type !== "ORDER" && !isIncomeType(tx.type)) continue;
    if (!byTicker[tx.ticker]) byTicker[tx.ticker] = [];
    byTicker[tx.ticker].push(tx);
  }
  const result = {};
  for (const [ticker, txs] of Object.entries(byTicker)) {
    result[ticker] = computeHoldingMetrics(txs);
  }
  return result;
}

// Unrealized capital gain on the open position; null if either input missing.
export function unrealizedGain(marketValue, costBasis) {
  if (marketValue === null || marketValue === undefined) return null;
  if (costBasis === null || costBasis === undefined) return null;
  return marketValue - costBasis;
}

// Sharesight-style total return %: (capital gain + income) / cost base.
export function totalReturnPct(capitalGain, income, costBasis) {
  if (!costBasis || costBasis <= EPSILON) return null;
  if (capitalGain === null || capitalGain === undefined) return null;
  return ((capitalGain + (income || 0)) / costBasis) * 100;
}

// Actual income transactions (broker-reported dividends/interest), grouped by
// calendar year. Amounts are in the account currency.
// Returns [{ year, gross, net, taxWithheld, count }] sorted by year desc.
export function incomeByYear(transactions) {
  const years = {};
  for (const tx of transactions || []) {
    if (!isIncomeType(tx.type) || !tx.timestamp) continue;
    const year = new Date(tx.timestamp).getFullYear();
    if (!years[year]) years[year] = { year, gross: 0, net: 0, taxWithheld: 0, count: 0 };
    const { gross, net } = incomeAmounts(tx);
    years[year].gross += gross ?? 0;
    years[year].net += net ?? 0;
    years[year].taxWithheld += tx.dividendWithheldTaxAmount ?? 0;
    years[year].count += 1;
  }
  return Object.values(years).sort((a, b) => b.year - a.year);
}

// All income transactions flattened for the income report detail table,
// sorted newest first.
export function incomeTransactions(transactions) {
  return (transactions || [])
    .filter((tx) => isIncomeType(tx.type))
    .slice()
    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
}

// All disposals across every ticker, flattened and sorted newest first.
// Each entry: { ticker, timestamp, quantity, proceeds, cost, gain, buyDates }.
export function allDisposals(metricsByTicker) {
  const rows = [];
  for (const [ticker, metrics] of Object.entries(metricsByTicker || {})) {
    for (const d of metrics.disposals) {
      rows.push({ ticker, currency: metrics.currency, ...d });
    }
  }
  return rows.sort((a, b) => b.timestamp - a.timestamp);
}

// Positions that have been completely sold out: open quantity ~0 with at
// least one disposal. Returns [{ ticker, buyCost, sellProceeds, realizedGain }].
export function soldPositions(metricsByTicker) {
  return Object.entries(metricsByTicker || {})
    .filter(([, m]) => m.quantity <= EPSILON && m.disposals.length > 0)
    .map(([ticker, m]) => ({
      ticker,
      currency: m.currency,
      buyCost: m.buyCost,
      sellProceeds: m.sellProceeds,
      realizedGain: m.realizedGain,
      lastSoldTs: m.disposals[m.disposals.length - 1].timestamp,
    }))
    .sort((a, b) => b.lastSoldTs - a.lastSoldTs);
}

// Sums a numeric field across rows grouped by their currency.
// Returns { [currency]: total } skipping rows where the value is null.
export function totalsByCurrency(rows, valueKey, currencyKey = "currency") {
  const totals = {};
  for (const row of rows || []) {
    const value = row[valueKey];
    const cur = row[currencyKey];
    if (value === null || value === undefined || !cur || cur === "-") continue;
    totals[cur] = (totals[cur] || 0) + value;
  }
  return totals;
}
